import { ComponentProps, useMemo, useState } from 'react'

import { GRID_CHECKBOX_SELECTION_COL_DEF, GridColumnVisibilityModel, GridRow, GridRowId, useGridApiRef } from '@mui/x-data-grid-premium'

import { CollapsibleHeaderContext, useCollapsibleHeaderState } from './CollapsibleHeader/CollapsibleHeaderContext'
import { DragDropGridContext, DragDropGridContextType } from './DragDropGrid/DragDropGridContext'
import { DraggableGridCell } from './DragDropGrid/DraggableGridCell'
import { DroppableGridRow } from './DragDropGrid/DroppableGridRow'
import { GridTableFooter } from './GridTableFooter'
import { GridRowActionItem } from './models/GridRowActions'
import { RowActionsContext } from './RowActionsGridCell/RowActionsContext'
import { RowActionsGridCell } from './RowActionsGridCell/RowActionsGridCell'
import { RowHotActionGridCell } from './RowActionsGridCell/RowHotActionGridCell'
import { StyledDataGrid } from './StyledDataGrid'

export type GridTableProps = ComponentProps<typeof StyledDataGrid> & Pick<DragDropGridContextType, 'onGridRowsDrop'> & {
  initialColumnGroupVisibility?: GridColumnVisibilityModel // Initial state of column group visibility (collapse or not collapse)
  initialColumnVisibility?: GridColumnVisibilityModel // Initial state of column visibility (visible or hidden)
} & {
  hotAction?: GridRowActionItem // Single action
  isDragDropEnabled?: boolean
  isRowActionsEnabled?: boolean
  children?: React.ReactNode
  pinnedLeftColumns?: string[]
  pinnedRightColumns?: string[]
}

export const GridTable: React.FC<GridTableProps> = ({
  isDragDropEnabled,
  isRowActionsEnabled,
  onGridRowsDrop,
  initialColumnGroupVisibility,
  initialColumnVisibility,
  columns,
  hotAction,
  slots,
  slotProps,
  pinnedLeftColumns = [],
  pinnedRightColumns = [],
  ...props
}) => {
  const [draggingRows, setDraggingRows] = useState<GridRowId[]>([])
  const apiRef = useGridApiRef()

  const {
    columnGroupVisibility,
    updateColumnGroupVisibility,
    columnVisibilityModel,
    setColumnVisibilityModel,
  } = useCollapsibleHeaderState(initialColumnGroupVisibility, initialColumnVisibility)

  const columnDefs = useMemo(() => {
    let updatedColumns = columns

    // Add drag and drop handle column pinned to the left
    if (isDragDropEnabled && updatedColumns) {
      const draggableColumnDef = {
        field: 'drag',
        headerName: '',
        width: 28,
        minWidth: 28,
        maxWidth: 48,
        renderCell: DraggableGridCell,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        filterable: false,
        hideSortIcons: true,
        disableReorder: true,
      }

      updatedColumns = [draggableColumnDef, ...updatedColumns]
    }

    // Add checkbox selection column pinned to the left
    if (props.checkboxSelection && updatedColumns) {
      const checkboxColumnDef = {
        ...GRID_CHECKBOX_SELECTION_COL_DEF,
        width: 28,
        minWidth: 28,
        maxWidth: 48,
      }

      updatedColumns = [checkboxColumnDef, ...updatedColumns]
    }

    // Add actions column pinned to the right
    if (isRowActionsEnabled) {
      const rowActionsColumnDef = {
        field: 'additionalActions',
        headerName: '',
        width: 28,
        minWidth: 28,
        maxWidth: 48,
        renderCell: RowActionsGridCell,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        filterable: false,
        hideSortIcons: true,
        disableReorder: true,
      }

      updatedColumns = [...updatedColumns, rowActionsColumnDef]
    }

    if (hotAction) {
      const hotActionColumnDef = {
        field: 'hotAction',
        headerName: '',
        width: 28,
        minWidth: 28,
        maxWidth: 48,
        renderCell: RowHotActionGridCell,
        disableColumnMenu: true,
        resizable: false,
        sortable: false,
        filterable: false,
        hideSortIcons: true,
        disableReorder: true,
      }

      updatedColumns = [...updatedColumns, hotActionColumnDef]
    }

    return updatedColumns
  }, [isDragDropEnabled, props.checkboxSelection, columns, hotAction, isRowActionsEnabled])

  // Disable column virtualization because it cause jankiness.
  // For some reason, setting column virtualization breaks column pinning.
  // useEffect(() => {
  //   if (apiRef.current) {
  //     apiRef.current.unstable_setColumnVirtualization(false)
  //   }
  // }, [apiRef])

  return (
    <CollapsibleHeaderContext.Provider value={{ columnGroupVisibility, updateColumnGroupVisibility, columnVisibilityModel, setColumnVisibilityModel }}>
      <DragDropGridContext.Provider value={{ draggingRows, setDraggingRows, onGridRowsDrop }}>
        <RowActionsContext.Provider value={{ hotAction: hotAction }}>
          <StyledDataGrid
            apiRef={apiRef}
            columns={columnDefs}
            rowHeight={28}
            columnHeaderHeight={28}
            columnVisibilityModel={columnVisibilityModel}
            rowReordering={false}
            disableAggregation={true}
            initialState={{
              pinnedColumns: {
                left: ['drag', GRID_CHECKBOX_SELECTION_COL_DEF.field, ...pinnedLeftColumns],
                right: ['hotAction', 'additionalActions', ...pinnedRightColumns],
              },
            }}
            onColumnVisibilityModelChange={(newModel) => {
              setColumnVisibilityModel(newModel)
            }}
            slotProps={{
              baseCheckbox: {
                disableRipple: true,
                sx: {
                  width: 28,
                  height: 28,
                },
              },
              ...slotProps,
            }}
            slots={{
              row: isDragDropEnabled ? DroppableGridRow : GridRow,
              footer: GridTableFooter,
              ...slots,
            }}
            isRowSelectable={({ row }) => row.isSelectable}
            getRowClassName={({ row }) => {
              let classNames = ''

              if (row.customStatus) {
                classNames += `orcoda-theme--${row.customStatus}`
              }

              if (row.textHighlight) {
                classNames += ' orcoda-theme--textHighlight'
              }

              return classNames
            }}
            {...props}
          />
        </RowActionsContext.Provider>
      </DragDropGridContext.Provider>
    </CollapsibleHeaderContext.Provider>
  )
}

